import { useEffect, useMemo, useRef, useState } from 'react'
import { useEditor } from '../store/useEditor'
import {
  deleteProject,
  duplicateProject,
  exportBackup,
  importBackup,
  listProjects,
  loadProject,
  setProjectStatus,
  type ProjectSummary,
} from '../lib/db'
import { JOB_STATUS, type JobStatus } from '../types/document'
import Customers from './Customers'
import { Empty, Loading, Sheet } from './bits'

import { IconChevron, IconMore } from './icons'

/**
 * Every job you have ever quoted.
 *
 * The search box matches name, customer and address together, because three
 * weeks after a walkthrough nobody remembers what they called the file - they
 * remember the name on the door or the street it was on.
 *
 * WHAT THIS IS FOR, BEYOND FINDING A PICTURE.
 *
 * A season is a hundred houses and most of them are waiting on an answer. A
 * list that only shows thumbnails cannot tell you which ones, so this shows
 * what each job is worth, how much of it there is, and where it stands - and
 * lets you move a job from quoted to won without opening it. That last part is
 * the difference between a gallery of pictures and something you run a season
 * out of.
 *
 * A JOB IS A ROW, NOT A TILE. Forty jobs read down a column in one pass; forty
 * tiles are a wall that has to be scanned. The photograph is still there,
 * small, because a person recognizes the house before they read the name.
 */
const money = (n: number) => `$${Math.round(n).toLocaleString()}`

/** Pill color follows what the status means, never a color on its own. */
const pillTone = (s: JobStatus) =>
  s === 'won' ? 'won' : s === 'lost' ? 'lost' : s === 'quoted' ? 'quoted' : ''

/**
 * TWO PLACES, ONE LIST.
 *
 * As a page inside the workspace it is what a contractor opens the app to. As
 * a sheet over the editor it is "find that house again" mid job. Same rows,
 * same numbers, same actions: two components would drift, and the drift would
 * be a job list that says one thing in one place and something else in the
 * other.
 *
 * embedded drops the backdrop, the close button and the Jobs/Customers tabs,
 * because the workspace already has navigation and does not need a second set
 * inside the page.
 */
export default function Projects({
  onClose,
  embedded = false,
  onStartNew,
  items: given,
  onRefresh,
  onOpenJob,
  title = 'Every house this season',
  lede = 'What each one is worth, and which ones are still waiting on an answer.',
}: {
  onClose: () => void
  embedded?: boolean
  /*
   * THE PAGE'S OWN WORDS, GIVEN BY WHOEVER IS SHOWING IT.
   *
   * Design & Quote used to render its own heading and then this list, which
   * rendered a second one underneath it: two titles, two ledes and two New
   * design buttons on one screen, plus a third in the header bar. One list,
   * one heading, and the destination says what the heading is.
   */
  title?: string
  lede?: string
  /** Embedded there is no sheet to close, so New job needs a door. */
  onStartNew?: () => void
  /** The workspace reads the jobs once and shares them with Customers. */
  items?: ProjectSummary[] | null
  onRefresh?: () => void
  /**
   * Embedded, a row opens the job's own page rather than the editor. As a
   * sheet over a job in progress there is no page to go to, so it still opens
   * the design.
   */
  onOpenJob?: (p: ProjectSummary) => void
}) {
  const [own, setOwn] = useState<ProjectSummary[] | null>(null)
  const items = given !== undefined ? given : own
  const [q, setQ] = useState('')
  const [filter, setFilter] = useState<'all' | JobStatus>('all')
  /*
   * THE JOBS NOBODY HAS NAMED.
   *
   * A job takes its customer from the quote screen, which is behind loading
   * the photograph, so a season of walkthroughs turns into a list of houses
   * called Untitled mockup. It is not a bug and it is exactly the thing that
   * makes this list useless in December, so the count says so out loud and
   * offers to show only those.
   */
  const [onlyUnnamed, setOnlyUnnamed] = useState(false)
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [busy, setBusy] = useState('')
  /*
   * The sheet still carries both, because over the editor there is no
   * navigation to reach the other question with.
   */
  const [tab, setTab] = useState<'jobs' | 'customers'>('jobs')
  const restoreRef = useRef<HTMLInputElement | null>(null)

  const currentId = useEditor((s) => s.doc.id)
  const loadDocument = useEditor((s) => s.loadDocument)
  const newDocument = useEditor((s) => s.newDocument)

  const refresh = () => {
    if (onRefresh) return onRefresh()
    listProjects().then(setOwn)
  }
  useEffect(() => {
    if (given === undefined) listProjects().then(setOwn)
  }, [given])

  const shown = useMemo(() => {
    if (!items) return []
    const needle = q.trim().toLowerCase()
    return items
      .filter((p) => filter === 'all' || p.status === filter)
      .filter((p) => !onlyUnnamed || !p.customer.trim())
      .filter((p) =>
        needle ? `${p.name} ${p.customer} ${p.address}`.toLowerCase().includes(needle) : true,
      )
  }, [items, q, filter, onlyUnnamed])

  /** How many have no name on them at all. Not filtered: the whole season. */
  const unnamed = useMemo(
    () => (items ?? []).filter((p) => !p.customer.trim()).length,
    [items],
  )

  /*
   * What the filtered view is worth, and what is still owed on it.
   *
   * Two different questions and a season needs both. "Worth" is every job on
   * screen at its quoted total, which is what filtering to Quoted is for.
   * "Owed" counts only invoices, and only the part that has not been paid,
   * because a quote nobody has accepted is not money anybody owes.
   */
  const sum = useMemo(() => shown.reduce((n, p) => n + (p.money?.total ?? 0), 0), [shown])
  const owed = useMemo(
    () => shown.reduce((n, p) => n + (p.money?.kind === 'invoice' ? p.money.balance : 0), 0),
    [shown],
  )

  const when = (iso: string) => {
    const d = new Date(iso)
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }

  const statusLabel = (s: JobStatus) => JOB_STATUS.find((x) => x.id === s)?.label ?? 'Working on it'

  /** Next status in the loop, so one tap moves a job along without a menu. */
  const nextStatus = (s: JobStatus): JobStatus => {
    const i = JOB_STATUS.findIndex((x) => x.id === s)
    return JOB_STATUS[(i + 1) % JOB_STATUS.length].id
  }

  const open = async (p: ProjectSummary) => {
    if (onOpenJob) {
      onOpenJob(p)
      return
    }
    const doc = await loadProject(p.id)
    if (doc) {
      loadDocument(doc)
      onClose()
    }
  }

  const startNew = () => {
    newDocument()
    if (onStartNew) onStartNew()
    onClose()
  }

  const backup = async () => {
    setBusy('Building the backup file...')
    try {
      const data = await exportBackup()
      const blob = new Blob([JSON.stringify(data)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const stamp = new Date().toISOString().slice(0, 10)
      a.download = `lightsos-backup-${stamp}.json`
      a.click()
      URL.revokeObjectURL(url)
      setBusy(`${data.jobs.length} ${data.jobs.length === 1 ? 'job' : 'jobs'} saved to the file.`)
    } catch {
      setBusy('The backup could not be built.')
    }
    setTimeout(() => setBusy(''), 4000)
  }

  const restore = async (file?: File) => {
    if (!file) return
    setBusy('Reading the file...')
    try {
      const res = await importBackup(JSON.parse(await file.text()))
      refresh()
      setBusy(
        `${res.added} restored` +
          (res.skipped ? `, ${res.skipped} already here` : '') +
          (res.failed ? `, ${res.failed} could not be read` : '') +
          '.',
      )
    } catch (err) {
      setBusy(err instanceof Error ? err.message : 'That file could not be read.')
    }
    setTimeout(() => setBusy(''), 5000)
  }

  const jobs = (
    <>
      {/*
        An empty title means the caller has already drawn the heading, which
        the Jobs destination does because its heading carries the List and
        Board pair. Rendering an empty h1 under it was a blank forty pixels.
      */}
      {embedded && title && (
        <div className="ws-head">
          <div>
            <h1>{title}</h1>
            <p>{lede}</p>
          </div>
        </div>
      )}

      {/*
        NO FIGURE CARDS ON A LIST PAGE. The home screen opens with them because
        a morning is a question about totals; a list page is a question about
        one house, and the design opens it straight into the filter row and the
        table. Three numbers restating the rows underneath them is furniture.
      */}
      <div className="ws-tools">
        <div className="ws-tabs">
          <button aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
            All
          </button>
          {JOB_STATUS.map((s) => (
            <button key={s.id} aria-pressed={filter === s.id} onClick={() => setFilter(s.id)}>
              {s.label}
            </button>
          ))}
        </div>
        {/*
            A COUNT, AND A WAY TO ACT ON IT, IN THE ROW WHERE FILTERS LIVE.

            This was a purple banner across the page saying 1 job has no
            customer name on it yet, with an underlined link in the middle of
            it. Nothing is broken - a contractor who takes twelve photographs
            on a Saturday and names them on Sunday is working correctly - so
            it is a filter, and it belongs with the filters.
          */}
          {embedded && unnamed > 0 && (
            <button
              className="ws-tab-alt"
              aria-pressed={onlyUnnamed}
              onClick={() => setOnlyUnnamed((v) => !v)}
            >
              No name yet
              <em>{unnamed}</em>
            </button>
          )}
        <input
          className="ws-search"
          type="search"
          placeholder="A name, a customer or a street"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {!embedded && (
          <button className="btn primary" onClick={startNew}>
            New job
          </button>
        )}
      </div>

      {items === null ? (
        <Loading what="Reading your jobs" />
      ) : shown.length === 0 ? (
        <div className="ws-card">
          {items.length === 0 ? (
            <Empty title="Nothing saved yet">Every mockup saves itself as you work.</Empty>
          ) : (
            <Empty kind="filtered" action={{ label: 'Clear the search', onClick: () => setQ('') }}>
              Nothing matches that.
            </Empty>
          )}
        </div>
      ) : (

        <div className="ws-card">
          {/*
            THE COLUMNS ARE NAMED. Every list in the design file says what its
            columns are above them; ours were four unlabeled tracks and the
            reader had to work out that the thing on the right was money.
          */}
          <div className="ws-thead" aria-hidden="true">
            <span />
            <span>House</span>
            <span>State</span>
            <span>Amount</span>
            <span />
          </div>
          {shown.map((p) => (
            <div key={p.id} className="ws-item" data-current={p.id === currentId || undefined}>
              <div className="ws-row" role="row">
                <button
                  className="ws-open"
                  onClick={() => void open(p)}
                  aria-label={`Open ${p.customer || p.name}`}
                >
                  <span className="ws-thumb">
                    {p.thumb ? <img src={p.thumb} alt="" /> : <i />}
                  </span>
                  {/*
                    THE SAME WORDS TWICE IS NOT A SECOND LINE.

                    A job with no customer and no street fell back to its own
                    name on BOTH lines, so a list of jobs nobody has named yet
                    read "Untitled mockup / Untitled mockup - Sep 6" thirty
                    times over and the photograph was carrying every bit of the
                    information on the row. Reported as: it feels like maybe it
                    should not have so many images there. The images were not
                    the problem; they were the only thing saying anything.

                    Whatever is on the top line never repeats underneath it.
                  */}
                  <span className="ws-name">
                    <strong>{p.customer || p.name || 'Untitled'}</strong>
                    <span>
                      {[
                        p.address,
                        when(p.savedAt),
                        p.money?.number || '',
                        p.customer && p.name && p.name !== 'Untitled mockup' ? p.name : '',
                      ]
                        .filter(Boolean)
                        .join('  ·  ')}
                    </span>
                  </span>
                  <span className={`ws-pill ${pillTone(p.status)}`}>{statusLabel(p.status)}</span>
                  {/*
                    THE LINE SOMEBODY OPENS THIS SCREEN TO READ. What it came
                    to, and what is left on it. A quote says what it is worth;
                    an invoice says what is owed, which are not the same claim
                    and should not read the same.
                  */}
                  <span className="ws-amount">
                    {p.money && p.money.total > 0 ? money(p.money.total) : ''}
                    {p.money?.kind === 'invoice' && p.money.balance > 0 && (
                      <em>{money(p.money.balance)} due</em>
                    )}
                    {p.money && p.money.paid > 0 && p.money.balance === 0 && <em>Paid</em>}
                  </span>
                </button>
                <button
                  className="ws-go"
                  aria-expanded={openId === p.id}
                  aria-label={`More for ${p.customer || p.name}`}
                  onClick={() => {
                    setOpenId(openId === p.id ? null : p.id)
                    setConfirmId(null)
                  }}
                >
                  {openId === p.id ? <IconChevron size={16} /> : <IconMore size={16} />}
                </button>
              </div>

              {openId === p.id && (
                <div className="ws-rowacts">
                  {/* One tap moves it along: working on it, quoted, won, lost. */}
                  <button
                    className="btn"
                    onClick={async () => {
                      await setProjectStatus(p.id, nextStatus(p.status))
                      refresh()
                    }}
                  >
                    Mark {statusLabel(nextStatus(p.status)).toLowerCase()}
                  </button>
                  {/*
                    Same house, second option. Showing a homeowner warm white
                    and then multicolor used to mean drawing the house twice or
                    overwriting the version he liked.
                  */}
                  <button
                    className="btn"
                    onClick={async () => {
                      const ok = await duplicateProject(p.id, `job_${Date.now().toString(36)}`)
                      refresh()
                      setBusy(ok ? 'Copied.' : 'That job could not be copied.')
                      setTimeout(() => setBusy(''), 3000)
                    }}
                  >
                    Copy
                  </button>
                  {confirmId === p.id ? (
                    <>
                      <button
                        className="btn danger"
                        onClick={async () => {
                          await deleteProject(p.id)
                          setConfirmId(null)
                          setOpenId(null)
                          refresh()
                        }}
                      >
                        Delete for good
                      </button>
                      <button className="btn ghost" onClick={() => setConfirmId(null)}>
                        Keep it
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn ghost"
                      aria-label={`Delete ${p.customer || p.name}`}
                      onClick={() => setConfirmId(p.id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/*
        THE ONLY COPY OF A SEASON SHOULD NOT BE ONE BROWSER.

        The jobs do sync, but a file the contractor keeps costs nothing and is
        the difference between a bad afternoon and a lost season on the day
        something goes wrong that nobody planned for.
      */}
      <div className="ws-housekeeping">
        <button className="link-btn" onClick={backup}>
          Back up every job to a file
        </button>
        <button className="link-btn" onClick={() => restoreRef.current?.click()}>
          Restore from a file
        </button>
        <input
          ref={restoreRef}
          type="file"
          accept="application/json,.json"
          hidden
          onChange={(e) => {
            restore(e.target.files?.[0])
            e.target.value = ''
          }}
        />
        {busy && <span className="ws-said">{busy}</span>}
      </div>
    </>
  )

  if (embedded) return jobs

  return (
    <Sheet
      title={tab === 'customers' ? 'Customers' : 'Jobs'}
      onClose={onClose}
      head={
        <div className="ws-tabs">
          <button aria-pressed={tab === 'jobs'} onClick={() => setTab('jobs')}>
            Jobs
          </button>
          <button aria-pressed={tab === 'customers'} onClick={() => setTab('customers')}>
            Customers
          </button>
        </div>
      }
    >
      {tab === 'customers' ? (
        <Customers items={items ?? []} onOpened={onClose} onChanged={refresh} />
      ) : (
        jobs
      )}
    </Sheet>
  )
}
